if (!customElements.get('search-load-more')) {
  class SearchLoadMore extends HTMLElement {
    constructor() {
      super();
      this.grid = null;
      this.button = null;
      this.loading = null;
      this.observer = null;
      this.isLoading = false;
      this.onClick = this.onClick.bind(this);
    }

    get isInfiniteMode() {
      return this.dataset.mode === 'infinite';
    }

    showLoadMoreFallback() {
      if (!this.button) return;

      this.button.classList.remove('dn');
      this.button.classList.add('dib');
      this.button.addEventListener('click', this.onClick);
    }

    connectedCallback() {
      this.grid = document.querySelector('#search-product-grid');
      this.button = this.querySelector('a[href]');
      this.loading = this.querySelector('.loading-overlay');
      if (!this.grid || !this.button || !this.dataset.url) return;

      if (this.isInfiniteMode) {
        this.button.classList.add('dn');
        this.button.classList.remove('dib');
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries && entries[0];
            if (!entry || !entry.isIntersecting) return;
            this.loadNextPage();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.button.addEventListener('click', this.onClick);
      }
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;

      if (isLoading) {
        this.setAttribute('aria-busy', 'true');
        if (this.button) this.button.setAttribute('aria-disabled', 'true');
        if (this.loading) {
          this.loading.hidden = false;
          this.loading.classList.remove('dn');
        }
        return;
      }

      this.removeAttribute('aria-busy');
      if (this.button) this.button.removeAttribute('aria-disabled');
      if (this.loading) {
        this.loading.hidden = true;
        this.loading.classList.add('dn');
      }
    }

    appendProducts(nextProducts) {
      const existingItems = this.grid.querySelectorAll('li[data-item-number]');
      const lastItemNumber =
        existingItems.length > 0
          ? parseInt(existingItems[existingItems.length - 1].getAttribute('data-item-number'), 10) || 0
          : 0;

      nextProducts.querySelectorAll('li[data-item-number]').forEach((item, index) => {
        item.setAttribute('data-item-number', String(lastItemNumber + index + 1));
      });

      this.grid.insertAdjacentHTML('beforeend', nextProducts.innerHTML);
    }

    updateNextUrl(url) {
      if (!url) {
        if (this.observer) this.observer.disconnect();
        this.remove();
        return false;
      }

      this.dataset.url = url;
      if (this.button) this.button.setAttribute('href', url);
      return true;
    }

    async fetchPage(url) {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Search load more failed: ${response.status}`);
      }

      const html = document.createElement('div');
      html.innerHTML = await response.text();

      const nextProducts = html.querySelector('#search-product-grid');
      const nextUrl = html.querySelector('.search-page-list')?.getAttribute('data-next-page-url') || '';

      return {
        products: nextProducts && nextProducts.innerHTML.trim().length ? nextProducts : null,
        nextUrl,
      };
    }

    async loadNextPage() {
      if (this.isLoading || !this.dataset.url) return;

      this.setLoading(true);

      try {
        let url = this.dataset.url;
        let appended = false;
        let attempts = 0;
        const maxEmptyPages = 5;

        while (url && attempts < maxEmptyPages) {
          attempts += 1;
          const page = await this.fetchPage(url);

          if (page.products) {
            this.appendProducts(page.products);
            appended = true;
          }

          if (!this.updateNextUrl(page.nextUrl)) return;
          url = page.nextUrl;

          if (appended) break;
        }

        if (!appended) this.remove();
      } catch (error) {
        console.error('SearchLoadMore: failed to load more products', error);
        if (this.isInfiniteMode) {
          if (this.observer) this.observer.disconnect();
          this.showLoadMoreFallback();
        }
      } finally {
        if (this.isConnected) this.setLoading(false);
      }
    }

    async onClick(event) {
      event.preventDefault();
      await this.loadNextPage();
    }
  }

  customElements.define('search-load-more', SearchLoadMore);
}
